import { Badge } from '@/shared/ui/badge';
import { AlertCircle } from 'lucide-react';
import { type Priority, isPriority } from '../constants/priority';

interface PriorityBadgeProps {
  priority: string | null;
}

const PRIORITY_COLOURS: Record<Priority, string> = {
  Critical: 'border-red-400 text-red-600 dark:border-red-800 dark:text-red-400',
  High: 'border-red-400 text-red-600 dark:border-red-800 dark:text-red-400',
  Medium: 'border-amber-400 text-amber-600 dark:border-amber-800 dark:text-amber-400',
  Low: 'border-gray-300 text-gray-500 dark:border-gray-700 dark:text-gray-400',
  // Dashed border so "not specified" is visually distinct from Low, which is a
  // real, deliberate low-urgency priority. The two used to render identically,
  // making every unset job look like it had been prioritised (SC-6369).
  Unknown: 'border-dashed border-gray-400 text-gray-500 dark:border-gray-600 dark:text-gray-400',
};

const ICON_PRIORITIES = new Set<Priority>(['Critical', 'High']);

export function PriorityBadge({ priority }: PriorityBadgeProps) {
  if (!isPriority(priority)) {
    // Missing or unrecognised name: fall back to the Unknown badge rather than
    // an em-dash placeholder, matching how the lookup table treats it.
    return (
      <span className="flex items-center gap-2">
        <Badge variant="outline" className={`bg-transparent ${PRIORITY_COLOURS.Unknown}`}>
          Unknown
        </Badge>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      {ICON_PRIORITIES.has(priority) && (
        <AlertCircle className="size-3.5 shrink-0 text-red-600 dark:text-red-400" />
      )}
      <Badge variant="outline" className={`bg-transparent ${PRIORITY_COLOURS[priority]}`}>
        {priority}
      </Badge>
    </span>
  );
}
